import * as React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

import {
  parseQueryParams,
  serializeQueryParams,
  useQueryParam,
} from '~publish/hooks/useQueryParam'
import { withCopiedSearchParams } from '~publish/legacy/routes/helpers/withCopiedSearchParams'

import {
  DEFAULT_DATE_RANGE,
  DEFAULT_SORT_OPTION,
  DEFAULT_STATUS,
  isDateRange,
} from './types'
import type {
  DateRange,
  Filters,
  FilterUpdates,
  SortOption,
  UseFiltersReturn,
  FilterStatus,
} from './types'
import { usePersistedStatusParam } from './usePersistedStatusParam'

/**
 * A convenience hook to access just the comment status from useFilters.
 * This makes it easier to consume the status in mutation hooks and other components
 * without being coupled to the full useFilters return shape.
 */
export const useCommentStatus = (): FilterStatus => {
  const { filters } = useFilters()
  return filters.status || DEFAULT_STATUS
}

/*
 * PURITY CONTRACT — read before adding code to this hook.
 *
 * useFilters is called concurrently from many components, so it MUST remain
 * a pure projection of the URL (plus a localStorage read for the persisted
 * status). Concretely, do not add:
 *
 *  - effects (`useEffect`): each instance would re-run them, firing
 *    duplicate navigations from possibly stale URL snapshots;
 *  - clock reads (`Date.now()` / `new Date()`): instances mounted at
 *    different times would disagree, fragmenting Apollo cache keys;
 *  - mount-time writes of any kind.
 *
 * Side effects belong elsewhere:
 *  - sync effects → PersistedStatusSync (mounted once per Community view);
 *  - interaction-time writes → inside the setter callbacks;
 *  - clock-derived values → getPublishedAtRange, at the query layer.
 */
export const useFilters = (): UseFiltersReturn => {
  const navigate = useNavigate()
  const location = useLocation()
  const [status, setStatus, persistStatus] = usePersistedStatusParam()

  const [sortOption, setSortOption] = useQueryParam<SortOption>('sort')
  const [publishedAtParam, setPublishedAt] =
    useQueryParam<DateRange>('publishedAt')
  // A value we no longer offer — a bookmarked `alltime`, a saved view from
  // before the window — resolves to the window rather than to no bounds.
  const publishedAt =
    publishedAtParam && isDateRange(publishedAtParam)
      ? publishedAtParam
      : DEFAULT_DATE_RANGE
  const [selectedChannelIds, setSelectedChannelIds] =
    useQueryParam<string[]>('channels')

  /**
   * Applies several filter changes in a single navigation. A key that is
   * omitted stays untouched; a key passed as `undefined` removes the param.
   *
   * Always use this over calling two individual setters in the same tick:
   * each setter builds the next URL from its own snapshot, so the second
   * navigation silently reverts the first.
   */
  const setFilters = React.useCallback(
    (updates: FilterUpdates): void => {
      // The current search is read from window.location at call time, not
      // from the render-captured router location: a render-time snapshot
      // goes stale when something else navigates in the same tick, and a
      // navigation built from a stale snapshot reverts that change.
      const params = parseQueryParams(window.location.search)

      const apply = (
        param: string,
        value: string | string[] | undefined,
      ): void => {
        if (value === undefined) {
          delete params[param]
        } else {
          params[param] = value
        }
      }

      if ('status' in updates) {
        apply('status', updates.status)
        // Mirror the param change into localStorage, including a clear —
        // otherwise dropping the status param would fall back to the stale
        // persisted value under PERSIST_STATUS_FILTER_SPLIT.
        persistStatus(updates.status)
      }
      if ('sortOption' in updates) {
        apply('sort', updates.sortOption)
      }
      if ('publishedAt' in updates) {
        apply('publishedAt', updates.publishedAt)
      }
      if ('selectedChannelIds' in updates) {
        apply('channels', updates.selectedChannelIds)
      }

      navigate({
        pathname: location.pathname,
        search: serializeQueryParams(params),
      })
    },
    [navigate, location.pathname, persistStatus],
  )

  const clearFilters = React.useCallback((): void => {
    // Clear the sort, date, and channel filters in a single navigation that
    // stays on the current path, so the active tab (comments/mentions) and
    // view (list/by-post) are preserved. Only `status` and `tab` are kept;
    // every other search param (sort, publishedAt, channels) is dropped.
    //
    // A single navigate is used rather than the individual useQueryParam
    // setters: those each read the same stale search snapshot, so calling them
    // together would race and the last write would clobber the others.
    navigate(
      withCopiedSearchParams(
        location.pathname,
        ['status', 'tab'],
        [],
        location.search,
      ),
    )
  }, [navigate, location.pathname, location.search])

  // "Unanswered first" sorts by status, which conflicts with a specific status
  // filter (the BE prioritizes sort over filter, breaking the filter). Coerce to
  // "newest" so consumers see the effective sort, not the stored preference.
  // The effective default also shifts to "newest" in that context — both are
  // computed together so hasActiveFilters can compare against the right default.
  const { effectiveSortOption, effectiveDefaultSortOption } =
    React.useMemo(() => {
      const raw = sortOption || DEFAULT_SORT_OPTION
      const hasSpecificStatus = status && status !== 'all'
      const effectiveDefault: SortOption = hasSpecificStatus
        ? 'newest'
        : DEFAULT_SORT_OPTION
      const effective: SortOption =
        hasSpecificStatus && raw === 'unansweredFirst' ? 'newest' : raw
      return {
        effectiveSortOption: effective,
        effectiveDefaultSortOption: effectiveDefault,
      }
    }, [sortOption, status])

  const hasActiveFilters = Boolean(
    effectiveSortOption !== effectiveDefaultSortOption ||
    publishedAt !== DEFAULT_DATE_RANGE ||
    (selectedChannelIds && selectedChannelIds.length > 0),
  )

  // Memoize the filter state to prevent unnecessary re-renders
  const filters: Filters = React.useMemo(
    () => ({
      status: status || DEFAULT_STATUS,
      sortOption: effectiveSortOption,
      publishedAt,
      selectedChannelIds,
    }),
    [status, effectiveSortOption, publishedAt, selectedChannelIds],
  )

  return React.useMemo(
    () => ({
      filters,
      actions: {
        setStatus,
        setSortOption,
        setPublishedAt,
        setSelectedChannelIds,
        setFilters,
      },
      utils: {
        clearFilters,
        hasActiveFilters,
      },
    }),
    [
      filters,
      setStatus,
      setSortOption,
      setPublishedAt,
      setSelectedChannelIds,
      setFilters,
      clearFilters,
      hasActiveFilters,
    ],
  )
}
